/**
 * Thin fetch wrapper that attaches the user's Entra access token + handles
 * standard error shapes from sc-platform.
 *
 * Token acquisition is silent when possible (the access token cached by
 * MSAL is reused); falls back to redirect when interaction is required.
 *
 * Pattern borrowed from ricky-curator: aggressive error handling, token
 * expiry checking, loop guards, and defensive redirects to prevent auth
 * hang/timeout loops that require manual cache clearing.
 */
import type { AccountInfo } from '@azure/msal-browser';
import type { ZodType } from 'zod';
import { apiTokenRequest, msalInstance } from './msalConfig.js';

const apiBaseUrl: string = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

/**
 * The API origin, for surfaces that SHOW the endpoint rather than call it —
 * the events handoff hands this to whoever owns a sending system.
 *
 * Falls back to the page's own origin rather than '': a relative `/api/events`
 * is fine for fetch (the dev proxy rewrites it) but useless in a request
 * someone is meant to copy into another system.
 */
export const apiOrigin: string = apiBaseUrl !== '' ? apiBaseUrl : window.location.origin;

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'ApiError';
  }
}

/** One Zod issue as the API sends it. Everything is optional on purpose: this
 *  parses a wire shape, so a missing field must degrade, never throw. */
interface WireIssue {
  path?: unknown;
  message?: unknown;
  code?: unknown;
}

/** Human-readable field name from a Zod path: `["subjectIds", 0]` → "subjectIds.0". */
const issuePath = (path: unknown): string =>
  Array.isArray(path) ? path.map((seg) => String(seg)).join('.') : '';

/**
 * A validation rejection, in words.
 *
 * The API answers a schema failure with `{ error: "validation_error", issues }`
 * and NO `message` — so falling through to `errBody.error` printed the bare
 * string "validation_error" on screen, which tells a curator nothing about
 * which field was wrong. The `issues` array is the only thing that does, and it
 * was being dropped on the floor.
 *
 * Returns undefined (not an empty string) when there is nothing usable, so the
 * caller's `??` chain falls through to the next candidate.
 */
export const describeIssues = (issues: unknown): string | undefined => {
  if (!Array.isArray(issues) || issues.length === 0) return undefined;
  const parts = (issues as WireIssue[])
    .map((issue) => {
      const message = typeof issue.message === 'string' ? issue.message : '';
      if (message.length === 0) return '';
      const field = issuePath(issue.path);
      return field.length > 0 ? `${field}: ${message}` : message;
    })
    .filter((part) => part.length > 0);
  if (parts.length === 0) return undefined;
  // Capped: a bulk payload can reject on hundreds of rows, and a banner that
  // long is as unreadable as the bare code was.
  const shown = parts.slice(0, 3);
  const extra = parts.length - shown.length;
  return extra > 0 ? `${shown.join('; ')} (+${String(extra)} more)` : shown.join('; ');
};

/**
 * Extract expiry time (seconds since epoch) from an access token payload.
 * Returns 0 if the token can't be parsed (invalid, malformed, etc).
 */
const accessTokenExp = (token: string): number => {
  const parts = token.split('.');
  if (parts.length !== 3) return 0;
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof payload.exp === 'number' ? payload.exp : 0;
  } catch {
    return 0;
  }
};

/**
 * Trigger interactive re-auth via redirect. Used when silent token acquisition
 * fails (timeout, network, invalid token, etc.). Entra's SSO cookie typically
 * bounces the user straight back after redirect, so this is usually invisible.
 *
 * Includes loop guard: sessionStorage flag prevents infinite redirect loops
 * when auth fundamentally can't succeed (invalid config, unreachable Entra, etc).
 * Flag is cleared on every successful silent token acquisition.
 *
 * If loginRedirect throws, clears MSAL cache and retries once (handles
 * interaction_in_progress from interrupted auth flows). If both attempts
 * fail, throws to let UI show Unauthorized screen.
 *
 * Returns never because successful redirect navigates away.
 */
const AUTH_RECOVERY_FLAG = 'sc-platform-admin:auth-recovery-at';
const AUTH_LOOP_GUARD_MS = 90_000;

const triggerInteractiveAuth = async (reason: string): Promise<never> => {
  const last = parseInt(sessionStorage.getItem(AUTH_RECOVERY_FLAG) ?? '0', 10);
  if (Number.isFinite(last) && Date.now() - last < AUTH_LOOP_GUARD_MS) {
    sessionStorage.removeItem(AUTH_RECOVERY_FLAG);
    console.warn(`[auth] recovery loop detected (${reason}); aborting redirect`);
    throw new ApiError(
      401,
      'Authentication failed. Please close this window and sign in again.',
      'auth_recovery_loop',
    );
  }

  sessionStorage.setItem(AUTH_RECOVERY_FLAG, String(Date.now()));
  console.warn(`[auth] re-authenticating: ${reason}`);

  try {
    await msalInstance.loginPopup(apiTokenRequest);
  } catch (err) {
    console.warn(`[auth] loginPopup threw (${reason}); clearing cache and retrying once`, err);
    try {
      msalInstance.clearCache();
    } catch {
      // best-effort
    }
    await msalInstance.loginPopup(apiTokenRequest);
  }

  // Unreachable in practice — loginPopup navigates or throws.
  throw new Error('redirecting to login');
};

/**
 * Returns a fresh access token. Aggressively falls back to interactive auth.
 *
 * Flow:
 * 1. Try silent acquisition. ANY error (timeout, network, interaction required,
 *    browser blocking) → redirect. Previous version only caught
 *    InteractionRequiredAuthError, missing timeouts and other issues.
 * 2. Check if cached token is at/near expiry (within 60s). If so, force refresh.
 * 3. On success, clear auth recovery flag (only fires if no successful acquisition
 *    has happened in 90s, which breaks further attempts).
 */
const getAccessToken = async (account: AccountInfo): Promise<string> => {
  let result;
  try {
    result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
  } catch (err) {
    console.warn(
      `[auth] acquireTokenSilent failed (${err instanceof Error ? err.name : 'unknown'}); redirecting`,
    );
    return triggerInteractiveAuth(
      `silent acquire failed: ${err instanceof Error ? err.name : 'unknown'}`,
    );
  }

  // Check if cached token is at or near expiry (within 60s).
  const exp = accessTokenExp(result.accessToken);
  const nowSec = Math.floor(Date.now() / 1000);
  if (exp > nowSec + 60) {
    // Token is fresh; clear recovery flag since we had a successful acquisition.
    sessionStorage.removeItem(AUTH_RECOVERY_FLAG);
    return result.accessToken;
  }

  // Token is at/near expiry — force a refresh. If this fails (most often hidden
  // iframe timeout due to third-party cookie blocking), redirect.
  try {
    const refreshed = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
      forceRefresh: true,
    });
    sessionStorage.removeItem(AUTH_RECOVERY_FLAG);
    return refreshed.accessToken;
  } catch (err) {
    console.warn(
      `[auth] forceRefresh acquireTokenSilent failed (${err instanceof Error ? err.name : 'unknown'}); redirecting`,
    );
    return triggerInteractiveAuth(
      `force-refresh failed: ${err instanceof Error ? err.name : 'unknown'}`,
    );
  }
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Override the API origin for this call. Defaults to VITE_API_BASE_URL
   * (the platform-api). The chat feature passes the orchestrator base so the
   * same Entra token is sent to a different service. See features/chat/agentClient.ts.
   */
  baseUrl?: string;
}

/**
 * Acquire the user's access token for callers that do their own `fetch` — e.g.
 * the streaming chat client, which can't use `apiRequest` because it reads an SSE
 * body chunk-by-chunk. Same account lookup + silent-acquire path as `apiRequest`.
 */
export const getAuthToken = async (): Promise<string> => {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new ApiError(401, 'No active account. Sign in first.');
  }
  return getAccessToken(account);
};

export const apiRequest = async <TResponse>(
  path: string,
  options: RequestOptions = {},
  // Optional response validator. When given, the JSON body is parsed through it
  // at the boundary — a malformed/drifted response throws instead of silently
  // flowing through as a bad `as TResponse` cast. Use lenient (passthrough)
  // schemas so benign server additions don't reject; see lib/schemas.ts.
  schema?: ZodType<unknown>,
): Promise<TResponse> => {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];
  if (!account) {
    throw new ApiError(401, 'No active account. Sign in first.');
  }

  const token = await getAccessToken(account);

  const base = options.baseUrl ?? apiBaseUrl;
  // FormData passes through untouched: the browser sets multipart/form-data
  // with its own boundary, and setting Content-Type ourselves would break it.
  const isFormData = options.body instanceof FormData;
  const response = await fetch(`${base}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    },
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const text = await response.text();
  // Infrastructure (a proxy 413, a gateway error page) answers with HTML, and
  // uploads are the path that draws it. Parsing before checking `ok` turned
  // those into a SyntaxError with no status attached.
  let json: unknown = null;
  try {
    json = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  } catch {
    if (!response.ok) {
      throw new ApiError(response.status, text.slice(0, 200) || response.statusText);
    }
    throw new ApiError(response.status, 'The server returned a response we could not read.');
  }

  if (!response.ok) {
    const errBody = json as { error?: string; message?: string; issues?: unknown } | null;
    throw new ApiError(
      response.status,
      errBody?.message ?? describeIssues(errBody?.issues) ?? errBody?.error ?? response.statusText,
      errBody?.error,
    );
  }

  if (schema) schema.parse(json);
  return json as TResponse;
};
